/**
 * Scroll slide-in animation composable
 * Creates reveal animation for elements as they enter viewport
 * Supports left-to-right and right-to-left directions with stagger
 * Respects user's reduced motion preference
 */

import { ref, onMounted, onUnmounted} from 'vue'

/** Slide-in animation direction */
export type SlideDirection = 'right-to-left' | 'left-to-right'

/** Slide-in animation options */
export interface SlideInAnimationOptions {
  threshold?: number
  rootMargin?: string
  direction?: SlideDirection
  visibleClass?: string
  staggerDelay?: number
}

/** Return type of useSlideInAnimation */
export interface UseSlideInAnimationReturn {
  registerElement: (el: HTMLElement | null | undefined) => void
  prefersReducedMotion: () => boolean
}

/**
 * Slide-in animation with IntersectionObserver
 * Animates elements as they enter viewport with directional slide
 * 
 * @param options - Animation configuration options
 * @returns Element registration function
 * 
 * @example
 * const { registerElement } = useSlideInAnimation({ direction: 'left-to-right' })
 * <div :ref="registerElement">Content</div>
 */
export function useSlideInAnimation(
  options: SlideInAnimationOptions = {}
): UseSlideInAnimationReturn {
  const {
    threshold = 0.1,
    rootMargin = '0px',
    direction = 'right-to-left',
    visibleClass = 'is-visible',
    staggerDelay = 200,
  } = options

  const elements = ref<HTMLElement[]>([])
  const observer = ref<IntersectionObserver | null>(null)

  // Determine animation class based on direction
  const animationClass = direction === 'left-to-right' ? 'slide-in-right' : 'slide-in-left'

  /**
   * Check if user prefers reduced motion
   * @returns True if reduced motion is preferred
   */
  const prefersReducedMotion = (): boolean => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  /**
   * Initialize IntersectionObserver for animation
   * @private
   */
  const initObserver = (): void => {
    // Skip animation if user prefers reduced motion
    if (prefersReducedMotion()) {
      elements.value.forEach(el => {
        el.classList.add(visibleClass)
        el.classList.add('no-animation')
      })
      return
    }

    observer.value = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = Math.max(0, elements.value.indexOf(entry.target as HTMLElement))

            // Add animation with stagger delay
            setTimeout(() => {
              entry.target.classList.add(visibleClass)
            }, index * staggerDelay)

            // Stop observing this element after animation
            observer.value?.unobserve(entry.target)
          }
        })
      },
      {
        threshold,
        rootMargin,
      }
    )

    // Start observing all elements
    elements.value.forEach(el => {
      observer.value?.observe(el)
    })
  }

  /**
   * Register element for animation
   * @param el - HTML element to animate
   */
  const registerElement = (el: HTMLElement | null | undefined): void => {
    if (el && !elements.value.includes(el)) {
      elements.value.push(el)
      el.classList.add(animationClass)
      
      // If observer exists, start observing this element immediately
      if (observer.value) {
        observer.value.observe(el)
      }
    }
  }

  /**
   * Cleanup observer
   * @private
   */
  const cleanup = (): void => {
    if (observer.value) {
      observer.value.disconnect()
    }
  }

  onMounted(() => {
    // Small delay to ensure all elements are registered
    setTimeout(initObserver, 100)
  })

  onUnmounted(() => {
    cleanup()
  })

  return {
    registerElement,
    prefersReducedMotion,
  }
}